/**
 * API Client with React Query
 * Handles API communication for local and deployed environments
 */

import { QueryClient, QueryFunctionContext } from '@tanstack/react-query';
import { getAuthHeaders } from './supabase-client';
import { useAuthStore } from '@/stores/auth-store';
import type { APIResponse, APIError } from '@/types';

const LOCAL_API_HOSTS = new Set(['localhost', '127.0.0.1']);
const NETLIFY_API_BASE_URL = '/.netlify/functions/api';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function getSupabaseStorageKey(): string | null {
  const supabaseUrl = (import.meta as ImportMeta & { env: Record<string, string | undefined> }).env
    .VITE_SUPABASE_URL;

  if (!supabaseUrl) {
    return null;
  }

  try {
    const hostname = new URL(supabaseUrl).hostname;
    const projectRef = hostname.split('.')[0]?.trim();

    return projectRef ? `sb-${projectRef}-auth-token` : null;
  } catch {
    return null;
  }
}

function extractAccessToken(value: unknown): string | null {
  if (!value) {
    return null;
  }

  if (isNonEmptyString(value)) {
    return value;
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const token = extractAccessToken(entry);
      if (token) {
        return token;
      }
    }

    return null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;

  const directToken = record.access_token ?? record.accessToken;
  if (isNonEmptyString(directToken)) {
    return directToken;
  }

  const nestedCandidates = [
    record.state,
    record.currentSession,
    record.session,
    record.data,
    record.persistedSession,
  ];

  for (const candidate of nestedCandidates) {
    const token = extractAccessToken(candidate);
    if (token) {
      return token;
    }
  }

  return null;
}

function parseStoredAccessToken(storedValue: string | null): string | null {
  if (!storedValue) {
    return null;
  }

  try {
    return extractAccessToken(JSON.parse(storedValue));
  } catch {
    return extractAccessToken(storedValue);
  }
}

function getPersistedAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const supabaseStorageKey = getSupabaseStorageKey();
    if (supabaseStorageKey) {
      const supabaseToken = parseStoredAccessToken(window.localStorage.getItem(supabaseStorageKey));

      if (supabaseToken) {
        return supabaseToken;
      }
    }

    const storedAuth = window.localStorage.getItem('auth-storage');
    const authStorageToken = parseStoredAccessToken(storedAuth);

    if (authStorageToken) {
      return authStorageToken;
    }

    return null;
  } catch {
    return null;
  }
}

function getLiveAccessToken(): string | null {
  const accessToken = useAuthStore.getState().session?.accessToken;

  return isNonEmptyString(accessToken) ? accessToken : null;
}

function hasAuthorizationHeader(headers: Record<string, string>): boolean {
  return Boolean(headers.Authorization || headers.authorization);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isAPIResponseEnvelope<T>(value: unknown): value is APIResponse<T> {
  return isRecord(value) && typeof value.success === 'boolean';
}

function normalizeAPIError(payload: unknown, fallbackMessage: string): APIError {
  if (isAPIResponseEnvelope<unknown>(payload) && payload.error) {
    return payload.error;
  }

  if (isRecord(payload)) {
    const message = payload.message;
    if (typeof message === 'string' && message.trim()) {
      return { code: 'HTTP_ERROR', message };
    }

    const detail = payload.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return { code: 'HTTP_ERROR', message: detail };
    }

    if (Array.isArray(detail) && detail.length > 0) {
      return { code: 'VALIDATION_ERROR', message: 'Request validation failed' };
    }
  }

  if (typeof payload === 'string' && payload.trim()) {
    return { code: 'HTTP_ERROR', message: payload };
  }

  return { code: 'UNKNOWN_ERROR', message: fallbackMessage };
}

function getAPIBaseURL(): string {
  const configuredURL = import.meta.env.VITE_API_URL;

  if (configuredURL) {
    try {
      const parsedURL = new URL(configuredURL);

      if (LOCAL_API_HOSTS.has(parsedURL.hostname)) {
        return configuredURL.replace(/\/+$/, '');
      }
    } catch {
      // Ignore invalid VITE_API_URL values and fall back to proxy routing.
    }
  }

  return NETLIFY_API_BASE_URL;
}

const API_BASE_URL = getAPIBaseURL();

// ============================================
// Query Client Configuration
// ============================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof APIClientError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// ============================================
// API Error Class
// ============================================

export class APIClientError extends Error {
  status: number;
  code: string;
  details?: Record<string, string[]>;
  requestId?: string;

  constructor(error: APIError, status: number) {
    super(error.message);
    this.name = 'APIClientError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
    this.requestId = error.requestId;
  }
}

// ============================================
// Core API Fetch Function
// ============================================

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
}

export async function apiFetch<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;

  // Build full URL
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint}`;

  // Prepare headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...((fetchOptions.headers as Record<string, string>) || {}),
  };

  // Add auth headers if not skipped
  if (!skipAuth) {
    const authHeaders = await getAuthHeaders();
    Object.assign(headers, authHeaders);

    if (!hasAuthorizationHeader(headers)) {
      const accessToken = getLiveAccessToken() ?? getPersistedAccessToken();

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
    }
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Parse response
    let data: unknown;
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      // Handle non-JSON responses
      const text = await response.text();
      data = {
        success: response.ok,
        data: text as unknown as T,
        error: response.ok ? undefined : { code: 'UNKNOWN_ERROR', message: text },
      };
    }

    // Handle errors
    if (!response.ok) {
      throw new APIClientError(normalizeAPIError(data, 'An unknown error occurred'), response.status);
    }

    if (isAPIResponseEnvelope<T>(data)) {
      if (!data.success) {
        throw new APIClientError(
          data.error || { code: 'UNKNOWN_ERROR', message: 'An unknown error occurred' },
          response.status
        );
      }

      return data.data as T;
    }

    return data as T;
  } catch (error) {
    // Re-throw APIClientError
    if (error instanceof APIClientError) {
      throw error;
    }

    // Wrap network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new APIClientError(
        { code: 'NETWORK_ERROR', message: 'Network error. Please check your connection.' },
        0
      );
    }

    // Wrap other errors
    throw new APIClientError(
      {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : 'An unknown error occurred',
      },
      500
    );
  }
}

// ============================================
// HTTP Method Helpers
// ============================================

export const api = {
  get: <T>(endpoint: string, options?: FetchOptions) =>
    apiFetch<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body: unknown, options?: FetchOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    }),

  put: <T>(endpoint: string, body: unknown, options?: FetchOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: <T>(endpoint: string, body: unknown, options?: FetchOptions) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: <T>(endpoint: string, options?: FetchOptions) =>
    apiFetch<T>(endpoint, { ...options, method: 'DELETE' }),
};

// ============================================
// React Query Helpers
// ============================================

// Create a query function for React Query
export function createQueryFn<T>(endpoint: string | ((ctx: QueryFunctionContext) => string)) {
  return async (context: QueryFunctionContext): Promise<T> => {
    const url = typeof endpoint === 'function' ? endpoint(context) : endpoint;
    return apiFetch<T>(url);
  };
}

// Create a mutation function for React Query
export function createMutationFn<T, V = unknown>(
  endpoint: string | ((variables: V) => string),
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST'
) {
  return async (variables: V): Promise<T> => {
    const url = typeof endpoint === 'function' ? endpoint(variables) : endpoint;
    return apiFetch<T>(url, {
      method,
      body: method !== 'DELETE' ? JSON.stringify(variables) : undefined,
    });
  };
}

// ============================================
// Query Key Factory
// ============================================

export const queryKeys = {
  // Listings
  listings: {
    all: ['listings'] as const,
    lists: (filters: Record<string, unknown>) => [...queryKeys.listings.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.listings.all, 'detail', id] as const,
    lead: (id: string) => [...queryKeys.listings.all, 'lead', id] as const,
  },

  // Leads
  leads: {
    all: ['leads'] as const,
    lists: (filters: Record<string, unknown>) => [...queryKeys.leads.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.leads.all, 'detail', id] as const,
    notes: (id: string) => [...queryKeys.leads.all, 'notes', id] as const,
    activities: (id: string) => [...queryKeys.leads.all, 'activities', id] as const,
    messages: (id: string) => [...queryKeys.leads.all, 'messages', id] as const,
    messagingAccounts: ['leads', 'messaging-accounts'] as const,
    messagingConnectSession: (id: string) => [...queryKeys.leads.all, 'messaging-connect-session', id] as const,
  },

  // Jobs
  jobs: {
    all: ['jobs'] as const,
    lists: (filters: Record<string, unknown>) => [...queryKeys.jobs.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.jobs.all, 'detail', id] as const,
    stats: ['jobs', 'stats'] as const,
    progress: (id: string) => [...queryKeys.jobs.all, 'progress', id] as const,
  },

  // Profiles
  profiles: {
    all: ['profiles'] as const,
    automation: ['profiles', 'automation'] as const,
    lists: (filters: Record<string, unknown>) => [...queryKeys.profiles.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.profiles.all, 'detail', id] as const,
  },

  // Dashboard
  dashboard: {
    stats: ['dashboard', 'stats'] as const,
    activity: ['dashboard', 'activity'] as const,
  },

  // Auth
  auth: {
    me: ['auth', 'me'] as const,
    session: ['auth', 'session'] as const,
  },
};

// ============================================
// Error Handler Helper
// ============================================

export function handleAPIError(error: unknown): { message: string; code: string } {
  if (error instanceof APIClientError) {
    return {
      message: error.message,
      code: error.code,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      code: 'UNKNOWN_ERROR',
    };
  }

  return {
    message: 'An unknown error occurred',
    code: 'UNKNOWN_ERROR',
  };
}
